import { md5 } from './md5';
import { Tiles3tzArchive, Tiles3tzTileset } from './types';

/** Reads byte ranges of a 3tz. */
export type RangeSource = {
  /** The bytes from `begin` to `end`, `end` exclusive. */
  read(begin: number, end: number): Promise<Uint8Array>;
  /** The file's last `length` bytes. */
  readTail(length: number): Promise<Uint8Array>;
};

const END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const ZIP64_LOCATOR = 0x07064b50;
const ZIP64_END_OF_CENTRAL_DIRECTORY = 0x06064b50;
const CENTRAL_HEADER = 0x02014b50;
const LOCAL_HEADER = 0x04034b50;
const INDEX_PATH = '@3dtilesIndex1@';
const INDEX_RECORD_BYTES = 24;
/** Always holds the end records, and usually the index's central record. */
const TAIL_BYTES = 256;

/** Reads a 3tz with HTTP range requests. */
export function httpRangeSource(url: string): RangeSource {
  const fetchRange = async (range: string) => {
    const response = await fetch(url, { headers: { Range: `bytes=${range}` } });
    if (response.status !== 206) {
      throw new Error(`Range ${range} of the 3tz failed: ${response.status}`);
    }
    return new Uint8Array(await response.arrayBuffer());
  };
  return {
    read: (begin, end) => fetchRange(`${begin}-${end - 1}`),
    readTail: (length) => fetchRange(`-${length}`),
  };
}

function view(bytes: Uint8Array) {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function getUint64(data: DataView, offset: number) {
  return Number(data.getBigUint64(offset, true));
}

/**
 * Opens a 3tz: its end records, the index and tileset.json, in four requests, see FORMAT.md
 * section 1.
 */
export async function openArchive(source: RangeSource): Promise<Tiles3tzArchive> {
  const tail = await source.readTail(TAIL_BYTES);
  const tailView = view(tail);
  // No archive comment, so the end record is the last 22 bytes
  const end = tail.length - 22;
  if (tailView.getUint32(end, true) !== END_OF_CENTRAL_DIRECTORY) {
    throw new Error('The 3tz has no end of central directory record');
  }

  let centralSize = tailView.getUint32(end + 12, true);
  let centralOffset = tailView.getUint32(end + 16, true);
  let fileSize = centralOffset + centralSize + 22;
  if (
    tailView.getUint16(end + 10, true) === 0xffff ||
    centralSize === 0xffffffff ||
    centralOffset === 0xffffffff
  ) {
    const locator = end - 20;
    if (tailView.getUint32(locator, true) !== ZIP64_LOCATOR) {
      throw new Error('The 3tz has no ZIP64 end of central directory locator');
    }
    const zip64EndOffset = getUint64(tailView, locator + 8);
    // It immediately precedes the locator, 56 bytes with no extensible data
    const zip64End = locator - 56;
    if (tailView.getUint32(zip64End, true) !== ZIP64_END_OF_CENTRAL_DIRECTORY) {
      throw new Error('The 3tz has no ZIP64 end of central directory record');
    }
    centralSize = getUint64(tailView, zip64End + 40);
    centralOffset = getUint64(tailView, zip64End + 48);
    fileSize = zip64EndOffset + tail.length - zip64End;
  }
  const tailStart = fileSize - tail.length;

  // The index is the central directory's last record: 46 + 15 bytes, or 12 more with a ZIP64 extra
  const centralEnd = centralOffset + centralSize;
  const recordStart = centralEnd - 46 - INDEX_PATH.length - 12;
  const records =
    recordStart >= tailStart
      ? tail.subarray(recordStart - tailStart, centralEnd - tailStart)
      : await source.read(recordStart, centralEnd);
  const { size: indexSize, headerOffset } = readIndexRecord(records);
  const indexStart = headerOffset + 30 + INDEX_PATH.length;
  const index = await source.read(indexStart, indexStart + indexSize);

  const tilesetOffset = lookupEntry(index, 'tileset.json');
  if (tilesetOffset === undefined) {
    throw new Error('The 3tz has no tileset.json');
  }
  const tileset = JSON.parse(
    new TextDecoder().decode(await readEntry(source, tilesetOffset)),
  ) as Tiles3tzTileset;

  return { fileSize, index, tileset };
}

/** The index's size and local header offset, from the end of the central directory. */
function readIndexRecord(bytes: Uint8Array) {
  const data = view(bytes);
  // The record is the last 61 bytes, or all 73 with a ZIP64 extra field
  const shortStart = bytes.length - 46 - INDEX_PATH.length;
  const start = data.getUint32(shortStart, true) === CENTRAL_HEADER ? shortStart : 0;
  const nameLength = data.getUint16(start + 28, true);
  const name = new TextDecoder().decode(bytes.subarray(start + 46, start + 46 + nameLength));
  if (data.getUint32(start, true) !== CENTRAL_HEADER || name !== INDEX_PATH) {
    throw new Error(`The 3tz's last entry is not ${INDEX_PATH}`);
  }

  let size = data.getUint32(start + 24, true);
  let headerOffset = data.getUint32(start + 42, true);
  // A ZIP64 extra field holds the saturated fields in order, after its 4-byte header
  let extra = start + 46 + nameLength + 4;
  if (size === 0xffffffff) {
    size = getUint64(data, extra);
    // The compressed size follows, the same for a stored entry
    extra += 16;
  }
  if (headerOffset === 0xffffffff) {
    headerOffset = getUint64(data, extra);
  }
  return { size, headerOffset };
}

/**
 * The local header offset of the entry at the path, by binary search of the index, whose records
 * are sorted by their MD5 read as two u64 little-endian words.
 */
export function lookupEntry(index: Uint8Array, path: string): number | undefined {
  const hash = view(md5(path));
  const low = hash.getBigUint64(0, true);
  const high = hash.getBigUint64(8, true);
  const data = view(index);

  let first = 0;
  let last = index.length / INDEX_RECORD_BYTES - 1;
  while (first <= last) {
    const middle = (first + last) >> 1;
    const record = middle * INDEX_RECORD_BYTES;
    const recordLow = data.getBigUint64(record, true);
    const recordHigh = data.getBigUint64(record + 8, true);
    if (recordLow === low && recordHigh === high) {
      return getUint64(data, record + 16);
    }
    if (recordLow < low || (recordLow === low && recordHigh < high)) {
      first = middle + 1;
    } else {
      last = middle - 1;
    }
  }
  return undefined;
}

/** An entry's bytes, from its local header, which gives its size. */
export async function readEntry(source: RangeSource, headerOffset: number): Promise<Uint8Array> {
  const header = view(await source.read(headerOffset, headerOffset + 30));
  if (header.getUint32(0, true) !== LOCAL_HEADER) {
    throw new Error(`No 3tz entry at ${headerOffset}`);
  }
  const size = header.getUint32(22, true);
  const start = headerOffset + 30 + header.getUint16(26, true) + header.getUint16(28, true);
  return source.read(start, start + size);
}
